/* ============================================================
   ErrorBoundary — last-resort render crash guard (TASK-196)

   A thrown error inside any component used to unmount the whole
   SPA (the "black screen" after quoting). This boundary keeps the
   shell alive and shows a readable fallback instead.
   ============================================================ */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surfaced in browser console / staging logs; not sent anywhere.
    console.error('[ErrorBoundary] render crash:', error, info.componentStack);
  }

  private handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div
          className="container"
          style={{
            paddingTop: 'var(--space-xl)',
            paddingBottom: 'var(--space-2xl)',
            maxWidth: '560px',
          }}
        >
          <div className="card" style={{ padding: 'var(--space-lg)', textAlign: 'center' }}>
            <h2
              style={{
                fontSize: 'var(--fs-h3)',
                fontWeight: 'var(--fw-semibold)',
                marginBottom: 'var(--space-sm)',
              }}
            >
              Что-то пошло не так
            </h2>
            <p
              style={{
                fontSize: 'var(--fs-body)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--space-sm)',
              }}
            >
              Раздел не удалось отобразить из-за внутренней ошибки. Остальные части
              сайта продолжают работать — обновите страницу, чтобы продолжить.
            </p>
            <p
              style={{
                fontSize: 'var(--fs-caption)',
                color: 'var(--color-text-tertiary)',
                marginBottom: 'var(--space-md)',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
            </p>
            <button className="btn btn-primary" onClick={this.handleReload}>
              Обновить страницу
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
